import React, { Suspense, lazy } from "react";
import { Redirect, Switch, useLocation } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { LayoutSplashScreen, ContentRoute } from "../_metronic/layout";
import { DashboardPage } from "./pages/DashboardPage";
import { Campioni } from "./pages/Campioni/Campioni";
import { Vendita } from "./pages/Vendita/Vendita";
import { File } from "./pages/File/File";
import { Medici } from "./modules/Admin/pages/medici/Medici";
import { Centri } from "./modules/Admin/pages/centri/Centri";
import { Clienti } from "./modules/Admin/pages/clienti/Clienti";
import AdminPage from "./modules/Admin/pages/AdminPage";
import { Calendario } from "./pages/Calendario";

import Ims from "./pages/Ims/ImsPage";
import ImsGuide from "./pages/Ims/ImsGuide";

import AgentAssistant from "./pages/AgentAssistant";
import LearningVideos from "./tw/learning/pages/LearningVideos";
import VideosList from "./tw/learning/pages/VideosList";
import QuizResultsPage from "./tw/learning/admin/QuizResultsPage";
import { SyncPage } from "./pages/RAG/SyncPage";
import { QueryPage } from "./pages/RAG/QueryPage";
import BudgetPage from "./pages/Budget/BudgetPage";
import { ApiDocsPage } from "./pages/ApiDocsPage";

import QuestionnaireModal from "./components/QuestionnaireModal";
import { useQuestionnaire } from "./hooks/useQuestionnaire";
import ProtectedRoute from "./components/ProtectedRoute";
import { isAgente } from "./config/config";
import { getRoutePermissions, learningResultsRoles, learningViewerRoles } from "./permissions";



//const AdminPage = lazy(() => import("./modules/Admin/pages/AdminPage"));

const UserProfilepage = lazy(() =>
  import("./modules/UserProfile/UserProfilePage")
);

export default function BasePage() {
  const { showQuestionnaire, questionnaireCompleted, isChecking, handleQuestionnaireComplete } = useQuestionnaire();
  const location = useLocation();
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const userIsAgente = isAgente(parseInt(user?.idRole, 10));
  const routePermissions = (path) =>
    getRoutePermissions(path, userIsAgente, user, location.hash);

  // useEffect(() => {
  //   console.log('Base page');
  // }, []) // [] - is required if you need only one call
  // https://reactjs.org/docs/hooks-reference.html#useeffect

  // Blocca l'accesso alle pagine se il questionario non è completato
  if (isChecking) {
    return <LayoutSplashScreen />;
  }

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <Switch>
        {
          /* Redirect from root URL to /dashboard. */
          /*<Redirect exact from="/" to="/dashboard" />*/
          <Redirect exact from="/" to="/dashboard" />
        }
        <ContentRoute path="/dashboard" component={DashboardPage} allowedRoles={routePermissions("/dashboard")} />
        <ProtectedRoute path="/ai-assistant" component={AgentAssistant} allowedRoles={routePermissions("/ai-assistant")} />
        <ProtectedRoute path="/learning-videos" component={LearningVideos} allowedRoles={learningViewerRoles} />
        {/* <ProtectedRoute path="/learning-videos" component={VideosList} /> */}
        <ProtectedRoute
          path="/admin/learning-results"
          component={QuizResultsPage}
          allowedRoles={learningResultsRoles}
        />
        <ProtectedRoute path="/ims" component={Ims} allowedRoles={routePermissions("/ims")} />
        <ProtectedRoute path="/budget" component={BudgetPage} allowedRoles={routePermissions("/budget")} />
        <ProtectedRoute path="/ims-videoguida" component={ImsGuide} allowedRoles={routePermissions("/ims-videoguida")} />
        <ProtectedRoute path="/admin" component={AdminPage} allowedRoles={routePermissions("/admin")} />
        <ProtectedRoute path="/medici" component={Medici} allowedRoles={routePermissions("/medici")} />
        <ProtectedRoute path="/centri" component={Centri} allowedRoles={routePermissions("/centri")} />
        <ProtectedRoute path="/centri/medici" component={Centri} allowedRoles={routePermissions("/centri/medici")} />
        <ProtectedRoute path="/file" component={File} allowedRoles={routePermissions("/file")} />
        <ProtectedRoute path="/calendario" component={Calendario.Calendario} allowedRoles={routePermissions("/calendario")} />
        <ProtectedRoute path="/agenda" component={Calendario.TabellaEventi} allowedRoles={routePermissions("/agenda")} />
        <ProtectedRoute path="/richiesta-ferie-permessi" component={Calendario.TabellaRichieste} />
        <ProtectedRoute path="/richiesta-campioni" component={Campioni.ShopCampioni} allowedRoles={routePermissions("/richiesta-campioni")} />
        <ProtectedRoute path="/gestione-campioni" component={Campioni.TabellaCampioni} allowedRoles={routePermissions("/gestione-campioni")} />
        <ProtectedRoute path="/richiesta-materiale" component={Campioni.ShopMateriale} allowedRoles={routePermissions("/richiesta-materiale")} />
        <ProtectedRoute
          path="/gestione-materiale"
          component={Campioni.TabellaMateriale}
          allowedRoles={routePermissions("/gestione-materiale")}
        />
        <ProtectedRoute path="/servizi" component={Campioni.Servizi} allowedRoles={routePermissions("/servizi")} />
        <ProtectedRoute
          path="/status-campionatura-servizi"
          component={Campioni.Richieste}
          allowedRoles={routePermissions("/status-campionatura-servizi")}
        />
        <ProtectedRoute
          path="/campioni-eventi"
          component={Campioni.CampioniEventi}
          allowedRoles={routePermissions("/campioni-eventi")}
        />
        <ProtectedRoute path="/richiesta-prodotti" component={Vendita.ShopProdotti} allowedRoles={routePermissions("/richiesta-prodotti")} />
        <ProtectedRoute path="/gestione-prodotti" component={Vendita.TabellaProdotti} allowedRoles={routePermissions("/gestione-prodotti")} />
        <ProtectedRoute path="/status-vendita" component={Vendita.RichiesteProdotti} allowedRoles={routePermissions("/status-vendita")} />
        <ProtectedRoute path="/clienti" component={Clienti} allowedRoles={routePermissions("/clienti")} />

        <ProtectedRoute path="/rag-sync" component={SyncPage} />
        <ProtectedRoute path="/rag-query" component={QueryPage} />
        <ContentRoute path="/api-docs" component={ApiDocsPage} />

        <ProtectedRoute path="/user-profile" component={UserProfilepage} />

        <Redirect to="error/404" />

        {/* <ContentRoute path="/builder" component={BuilderPage} /> */}
      </Switch>
      
      {/* Questionario che appare dopo il login */}
      <QuestionnaireModal 
        show={showQuestionnaire}
        onHide={() => {}} // Non permettere di chiudere il questionario
        onComplete={handleQuestionnaireComplete}
      />
    </Suspense>
  );
}
